import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { deleteReclaim, overrideApproveReclaim, withdrawReclaimAdmin } from 'actions/reclaims'
import { useAppDispatch } from 'hooks/hooks'
import { useState } from 'react'

// CS override actions on an already-decided (or open) reclaim that need a mandatory remark.
export type DecidedActionId = 'overrideApprove' | 'withdraw' | 'delete'

interface DecidedConfig {
    heading: string
    prompt: string
    confirmLabel: string
    requiresRemark: boolean
    danger: boolean
}

const DECIDED_CONFIG: Record<DecidedActionId, DecidedConfig> = {
    overrideApprove: {
        heading: 'Approve reclaim',
        prompt: 'Provide a mandatory remark to approve this reclaim.',
        confirmLabel: 'Approve',
        requiresRemark: true,
        danger: false,
    },
    withdraw: {
        heading: 'Withdraw reclaim',
        prompt: 'Provide a mandatory remark to withdraw this reclaim.',
        confirmLabel: 'Withdraw',
        requiresRemark: true,
        danger: false,
    },
    delete: {
        heading: 'Delete reclaim',
        prompt: 'Provide a mandatory remark to delete this reclaim.',
        confirmLabel: 'Delete',
        requiresRemark: true,
        danger: true,
    },
}

interface Props {
    reclaimId: number
    action: DecidedActionId
    onSuccess?: () => void
}

const ReclaimDecidedActionModal = NiceModal.create(({ reclaimId, action, onSuccess }: Props) => {
    const modal = useModal()
    const dispatch = useAppDispatch()
    const [remark, setRemark] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const config = DECIDED_CONFIG[action]

    const onClose = () => {
        if (loading) {
            return
        }
        modal.resolve()
        modal.hide()
    }

    const onConfirm = () => {
        setLoading(true)
        setError(null)

        const thunk = (() => {
            if (action === 'overrideApprove') {
                return overrideApproveReclaim(reclaimId, remark)
            }
            if (action === 'withdraw') {
                return withdrawReclaimAdmin(reclaimId, remark)
            }
            return deleteReclaim(reclaimId, remark)
        })()

        dispatch(thunk).then(
            () => {
                setLoading(false)
                onSuccess?.()
                modal.resolve()
                modal.hide()
            },
            (response: { error?: string } = {}) => {
                setLoading(false)
                setError(response.error || 'Something went wrong')
            },
        )
    }

    const confirmDisabled = loading || (config.requiresRemark && remark.trim().length < 1)

    return (
        <BaseModal onClose={onClose} maxWidth={480}>
            <BaseModalHeader>{config.heading}</BaseModalHeader>
            <BaseModalMainContent>
                <p>{config.prompt}</p>
                {config.requiresRemark && (
                    <TextAreaField
                        autoFocus
                        value={remark}
                        onChange={({ target }) => setRemark(target.value)}
                        placeholder="Remark (required)"
                        style={{ marginTop: 12 }}
                    />
                )}
                {error && (
                    <p className="text-red-500" style={{ marginTop: 12 }}>
                        {error}
                    </p>
                )}
            </BaseModalMainContent>
            <BaseModalFooter className="row justify-end gap-2">
                <Button appearance="secondary" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    className={config.danger ? 'danger' : undefined}
                    appearance={config.danger ? undefined : 'primary'}
                    disabled={confirmDisabled}
                    onClick={onConfirm}>
                    {loading ? <LoadingDots color="#dadada" /> : config.confirmLabel}
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default ReclaimDecidedActionModal
